"use client";

import { useEffect, useRef } from "react";

/* Phase 4.5 — one Escape key, one dismissal.
 *
 * Modal, Drawer and Popover each attached their own
 * `document.addEventListener("keydown", …)` and called `e.stopPropagation()`
 * inside it. That call does nothing useful here: stopPropagation blocks
 * further travel to OTHER nodes, but every one of these listeners is bound to
 * the same node (document), and listeners on the same node all still run. So
 * one Escape closed every open overlay simultaneously — a confirm dialog
 * stacked on a modal took the modal down with it, and the analyst lost the
 * form they were confirming (finding F16).
 *
 * The fix is a single shared stack. Each overlay pushes an entry while open
 * and pops it when it closes; exactly one document listener exists, and it
 * only ever calls the handler on the LAST entry. Ordering is by mount, which
 * is the same as visual stacking here — an overlay opened on top of another
 * is always mounted after it.
 *
 * d2d-XDR eval F17: the listener used to run in the CAPTURE phase and call
 * stopPropagation, which got ahead of every other Escape handler — so inside
 * a Modal or Drawer, Escape never cleared a SearchField, never closed a
 * Combobox's list first, and never dismissed a Tooltip (a WCAG 1.4.13
 * failure). It now listens in the BUBBLE phase and skips any Escape a
 * component already handled (`defaultPrevented`): the focused control gets
 * the first say, and the top overlay closes only if nothing claimed the key.
 * A component that consumes Escape must call preventDefault().
 */

type Entry = { id: symbol; onEscape: () => void };

const stack: Entry[] = [];
let listening = false;

function onKeyDown(e: KeyboardEvent) {
  if (e.key !== "Escape" || e.defaultPrevented) return;
  const top = stack[stack.length - 1];
  if (!top) return;
  e.preventDefault();
  e.stopPropagation();
  top.onEscape();
}

function ensureListener() {
  if (listening || typeof document === "undefined") return;
  document.addEventListener("keydown", onKeyDown);
  listening = true;
}

function teardownListener() {
  if (!listening || stack.length > 0 || typeof document === "undefined") return;
  document.removeEventListener("keydown", onKeyDown);
  listening = false;
}

/** Number of overlays currently open. Used for body scroll-lock refcounting. */
export function overlayDepth(): number {
  return stack.length;
}

/**
 * Registers an overlay on the dismissal stack for as long as `open` is true.
 * Only the top-most registered overlay receives Escape.
 */
export function useEscapeStack(open: boolean, onEscape: () => void): void {
  // The handler is read through a ref so that a caller passing an inline
  // arrow function does not churn the stack on every render — re-pushing an
  // entry would silently reorder it above overlays opened after it.
  const handlerRef = useRef(onEscape);
  useEffect(() => {
    handlerRef.current = onEscape;
  });

  useEffect(() => {
    if (!open) return;
    const entry: Entry = { id: Symbol("overlay"), onEscape: () => handlerRef.current() };
    stack.push(entry);
    ensureListener();
    return () => {
      const i = stack.findIndex((e) => e.id === entry.id);
      if (i !== -1) stack.splice(i, 1);
      teardownListener();
    };
  }, [open]);
}
